import {debounce} from '@github/mini-throttle'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {showGlobalError} from './behaviors/ajax-error'
import {trackView} from './hydro-tracking'
import './dashboard/feed/feed-container-element'
import type {FeedContainerElement} from './dashboard/feed/feed-container-element'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

on('click', '.js-follow-unfollow-submit', async function (event) {
  const button = event.currentTarget as HTMLButtonElement
  const form = button.form as HTMLFormElement
  const id = button.id
  if (id) {
    const otherId = id.includes('unfollow') ? id.replace('unfollow', 'follow') : id.replace('follow', 'unfollow')
    const otherButton = document.getElementById(otherId)
    if (otherButton) {
      otherButton.focus()
    }
  }
  event.preventDefault()
  let response
  try {
    response = await fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        Accept: 'application/json',
        ...getBaseFetchHeaders(),
      },
    })
  } catch {
    showGlobalError()
  }
  if (button.classList.contains('js-hide-heading-menu-button')) {
    button.hidden = true
  }
  if (response && !response.ok) {
    showGlobalError()
  }
})

on('click', '.js-feed-post-active-topic-remove', function () {
  updateSelectedTopicInForm(null, null)
})

function updateSelectedTopicInForm(name: string | null, id: string | null) {
  const activeTopicId = document.querySelector('.js-feed-post-form-selected-topic-id') as HTMLInputElement
  const activeTopicName = document.querySelector('.js-feed-post-form-selected-topic-name') as HTMLInputElement
  const displayedTopicTagName = document.querySelector('.js-feed-post-active-tag-name') as HTMLElement
  const displayedTopicTagWrapper = document.querySelector('.js-feed-post-active-tag-wrapper') as HTMLElement

  if (!activeTopicId || !activeTopicName || !displayedTopicTagName || !displayedTopicTagWrapper) return

  if (id && name) {
    activeTopicId.value = id
    activeTopicName.value = name
    displayedTopicTagName.textContent = name
    displayedTopicTagWrapper.removeAttribute('hidden')
  } else {
    activeTopicId.removeAttribute('value')
    activeTopicName.removeAttribute('value')
    displayedTopicTagWrapper.setAttribute('hidden', 'true')
  }
}

// feed card visibility tracking
const cards: {[key: string]: ReturnType<typeof setTimeout>} = {}
const visibleThreshold = 0.8 // amnt of the card element in the viewport for it to be considered visible
const minimumReadTime = 1000 // 1 second
const observer = new IntersectionObserver(
  function (entries) {
    for (const entry of entries) {
      const el = entry.target as HTMLElement
      const hydroAttrs = el.getAttribute('data-hydro-view')
      if (!hydroAttrs) return

      const id = JSON.parse(hydroAttrs).payload.feed_card.card_retrieved_id
      const isVisible = entry.intersectionRatio > visibleThreshold

      if (!isVisible && cards[id]) {
        clearTimeout(cards[id])
        delete cards[id]
      }

      if (isVisible && !cards[id]) {
        cards[id] = setTimeout(() => {
          trackView(el)
        }, minimumReadTime)
      }
    }
  },
  {
    rootMargin: '0px',
    threshold: [0, visibleThreshold],
  },
)

observe('.js-feed-item-view', el => observer.observe(el))

// heading menu button animations
let unhiddenHeadingMenus: HTMLDetailsElement[] = []

function getHeadingMenuButton(card: HTMLElement): HTMLDetailsElement | null {
  const menu = card.getElementsByClassName('js-feed-item-heading-menu')
  if (menu.length === 0) return null
  return menu[0] as HTMLDetailsElement
}

const debouncedShowMenuButton = debounce((card: HTMLElement) => {
  const menu = getHeadingMenuButton(card)
  if (!menu) return

  menu.hidden = false
  unhiddenHeadingMenus.push(menu)
}, 100)

const debouncedHideMenuButton = debounce(() => {
  hideAllHeadingMenus()
}, 100)

function hideAllHeadingMenus() {
  const visibleMenus = []
  for (const menu of unhiddenHeadingMenus) {
    // if menu is open, please don't hide it
    const card = menu.closest('.js-feed-item-component') as HTMLElement

    if (menu.open || card.hasAttribute('focus-child')) {
      visibleMenus.push(menu)
    } else {
      menu.hidden = true
    }
  }

  unhiddenHeadingMenus = visibleMenus
}

observe('.js-feed-item-component', e => {
  e.addEventListener('mouseenter', (evt: Event) => {
    const card = evt.target as HTMLElement
    debouncedShowMenuButton(card)
  })
  e.addEventListener('mouseleave', debouncedHideMenuButton)
})

on('click', 'js-feed-item-next-component', function () {
  sessionStorage.setItem('github:dashboard:scrollHeight', `${window.scrollY}`)
})

on('click', '.js-feed-item-heading-menu', e => {
  const template = e.currentTarget.querySelector<HTMLTemplateElement>('template')

  if (!template) return
  const templateContainerContent = template.content
  const target = e.currentTarget.querySelector('.js-block-user-button')
  target?.append(templateContainerContent)
})

on('click', '.js-feed-filter-trigger', function () {
  const menu = document.querySelector<HTMLDetailsElement>('.js-feed-filter-menu')
  if (!menu) return
  menu.open = true
})

async function submitFeedDisinterest(target: EventTarget & Element): Promise<void | {response: Response}> {
  const form = target.parentElement as HTMLFormElement
  if (!form) return

  const formData = new FormData(form)
  const eventId = formData.get('event_id')
  const reasonDismissed = formData.get('reason_dismissed')
  const cardType = formData.get('card_type')
  if (!eventId) return

  let response
  try {
    response = await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        Accept: 'application/json',
        ...getBaseFetchHeaders(),
      },
    })
  } catch {
    showGlobalError()
  }
  if (response && !response.ok) {
    showGlobalError()
  } else {
    if (reasonDismissed === 'dismissed' && cardType === 'create_discussion') {
      removeAnnouncementCard(eventId)
    } else {
      // close the dialog and menu
      toggleDismissedPlaceholder(eventId)
    }
    form.closest<HTMLDialogElement>('.js-feed-item-disinterest-dialog')?.close()
    form.closest('.js-feed-item-heading-menu')?.removeAttribute('open')
  }
}

function toggleDismissedPlaceholder(event_id: FormDataEntryValue) {
  const card = document.querySelector(`#feed-item-${event_id}`)
  const showMore = card?.parentElement?.querySelector(':scope > .js-details-target')

  const placeholder = document.querySelector(`#dismissed-item-placeholder-${event_id}`)
  if (!card || !placeholder) return

  card.toggleAttribute('hidden')
  placeholder.toggleAttribute('hidden')
  showMore?.toggleAttribute('hidden')
}

function removeAnnouncementCard(event_id: FormDataEntryValue) {
  const card = document.querySelector(`#feed-item-${event_id}`)?.parentElement
  const prevCard = card?.previousElementSibling
  const nextCard = card?.nextElementSibling

  if (!card) return

  card.addEventListener('transitionend', () => {
    card.remove()

    const totalCards = document.querySelectorAll('.js-pinned-announcement-container').length
    // Hide Show all/less if there are less than 3 announcements
    if (totalCards < 3) {
      document.querySelector('.js-pinned-announcements-toggle')?.toggleAttribute('hidden', true)
    }
    // Show "No new announcements yet" if there are no more pinned announcements
    if (!nextCard && !prevCard) {
      document.querySelector('#dismissed-announcements-placeholder')?.removeAttribute('hidden')
      return
    }

    if (card.classList.contains('Details-content--hidden')) {
      return
    } else if (nextCard) {
      slideAnnouncementCard(nextCard)
    }
  })
  card.classList.add('transition')
}

function slideAnnouncementCard(nextCard: Element) {
  if (nextCard.classList.contains('Details-content--hidden')) {
    nextCard.classList.remove('Details-content--hidden')
  } else {
    const nextNextCard = nextCard.nextElementSibling
    if (nextNextCard && nextNextCard.classList.contains('Details-content--hidden')) {
      nextNextCard.classList.remove('Details-content--hidden')
    }
  }
}

on('click', '.js-feed-disinterest-submit', function (e) {
  e.preventDefault()

  submitFeedDisinterest(e.currentTarget)
})

on('click', '.js-disinterest-checkbox', function (e) {
  const checkbox = e.currentTarget as HTMLInputElement
  const form = checkbox.closest('form')
  if (!form) return

  const submit = form.querySelector<HTMLButtonElement>('.js-feed-disinterest-submit')
  if (!submit) return

  const checkboxes = form.querySelectorAll<HTMLInputElement>('.js-disinterest-checkbox')
  // if all checkboxes are unchecked, disable the submit button
  submit.disabled = Array.from(checkboxes).every(cb => !cb.checked)
})

// hide details popups if the user scrolls through the sidebar
const leftColumnDetailsObserver = new IntersectionObserver(
  entries => {
    for (const entry of entries) {
      entry.target.toggleAttribute('open', false)
    }
  },
  {
    root: document.querySelector('.js-left-column-scroll-container'),
  },
)

observe('.js-left-column-scroll-container details', el => leftColumnDetailsObserver.observe(el))
